import { useState, useEffect, useRef } from "react";
import { CheckCircle2, MessageSquare, XCircle, Loader2, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { SignAgreement, SignedBadge } from "@/components/webadvisor/SignAgreement";

const RESPONSE_COPY = {
  accepted: { icon: CheckCircle2, cls: "bg-green-50 border-green-200 text-green-800", title: "Proposal accepted", body: "Thank you! Your deposit is received and your advisor will reach out to kick things off." },
  changes_requested: { icon: MessageSquare, cls: "bg-amber-50 border-amber-200 text-amber-800", title: "Changes requested", body: "Your advisor has your note and will follow up with an updated plan." },
  declined: { icon: XCircle, cls: "bg-slate-50 border-slate-200 text-slate-700", title: "Proposal declined", body: "Thanks for letting us know. You can still accept below if you change your mind." },
};

const usePaymentReturn = (token, onPaid) => {
  const [checking, setChecking] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid || started.current) return;
    started.current = true;
    setChecking(true);
    (async () => {
      let done = false;
      for (let i = 0; i < 10 && !done; i++) {
        try {
          const { data } = await api.get(`/api/webadvisor/public/proposal/${token}/payment/${sid}`);
          if (data.payment_status === "paid") { onPaid(); toast.success(data.kind === "final" ? "Balance paid — thank you!" : "Payment received — proposal accepted!"); done = true; break; }
          if (data.status === "expired") { toast.error("Payment session expired — please try again"); done = true; break; }
        } catch { /* transient — keep polling */ }
        await new Promise(r => setTimeout(r, 2500));
      }
      setChecking(false);
      if (done) window.history.replaceState({}, "", window.location.pathname);
      else toast.error("We couldn't confirm your payment yet. If you were charged, refresh this page in a minute.", { duration: 10000 });
    })();
  }, [token, onPaid]);
  return checking;
};

export const ProposalResponse = ({ token, plan, invoice, signature, onChange }) => {
  const [mode, setMode] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const checking = usePaymentReturn(token, onChange);
  const resp = plan.prospect_response;
  const deposit = invoice?.deposit_due || 0;
  const pct = invoice?.deposit_pct ?? 50;

  const submit = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/webadvisor/public/proposal/${token}/respond`, { action, name: action === "accept" ? signature?.name || name : name, note, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
      toast.success("Thanks — your advisor has been notified"); setMode(null); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(false); }
  };

  if (checking) return <div className="bg-white border border-slate-200 rounded-xl p-6 flex items-center gap-3 text-slate-600" data-testid="proposal-payment-checking"><Loader2 className="w-5 h-5 animate-spin" />Confirming your payment…</div>;
  const copy = resp && RESPONSE_COPY[resp.action];
  if (resp?.action === "accepted") return <ResponseBanner copy={copy} />;

  return (
    <div id="respond" className="scroll-mt-6 bg-white border-2 border-blue-600 rounded-xl p-6 shadow-sm space-y-4" data-testid="proposal-response">
      {copy && <ResponseBanner copy={copy} />}
      <div>
        <p className="text-lg font-bold text-slate-900">Ready to move forward?</p>
        <p className="text-sm text-slate-500" data-testid="proposal-deposit-copy">{pct >= 100
          ? `Accept to lock in this plan with a single $${deposit.toLocaleString()} payment (paid in full upfront).`
          : `Accept to lock in this plan with a $${deposit.toLocaleString()} deposit (${pct}% of the project). The rest is due on delivery.`}</p>
      </div>
      {mode === "accept" && (signature ? <SignedBadge token={token} signature={signature} /> : <SignAgreement token={token} defaultName={name} onSigned={onChange} />)}
      {mode && (
        <div className="space-y-2">
          {mode !== "accept" && <Input placeholder="Your name" value={name} onChange={e => setName(e.target.value)} data-testid="proposal-response-name" />}
          <Textarea rows={3} placeholder={mode === "accept" ? "Anything we should know? (optional)" : mode === "changes" ? "What would you like changed? (optional)" : "Mind telling us why? (optional)"} value={note} onChange={e => setNote(e.target.value)} data-testid="proposal-response-note" />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => (mode === "accept" ? submit("accept") : setMode("accept"))} disabled={busy || deposit <= 0 || (mode === "accept" && !signature)} title={mode === "accept" && !signature ? "Sign the agreement first" : ""} className="bg-green-600 hover:bg-green-500 text-white font-bold text-sm px-5 py-2.5 rounded-lg flex items-center gap-2 disabled:opacity-50" data-testid="proposal-accept-btn">
          {busy && mode === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}{mode === "accept" ? `Pay $${deposit.toLocaleString()} ${pct >= 100 ? "now" : "deposit"}` : "Accept proposal"}
        </button>
        <button onClick={() => (mode === "changes" ? submit("changes") : setMode("changes"))} disabled={busy} className="border border-amber-300 text-amber-800 hover:bg-amber-50 font-medium text-sm px-4 py-2.5 rounded-lg disabled:opacity-50" data-testid="proposal-changes-btn">{mode === "changes" ? "Send request" : "Request changes"}</button>
        <button onClick={() => (mode === "decline" ? submit("decline") : setMode("decline"))} disabled={busy} className="text-slate-500 hover:text-red-600 font-medium text-sm px-3 py-2.5 disabled:opacity-50" data-testid="proposal-decline-btn">{mode === "decline" ? "Confirm decline" : "Decline"}</button>
        {mode && <button onClick={() => setMode(null)} className="text-xs text-slate-400 hover:text-slate-600 px-2" data-testid="proposal-response-cancel">Cancel</button>}
      </div>
    </div>
  );
};

const ResponseBanner = ({ copy }) => {
  const Icon = copy.icon;
  return (
    <div className={`border rounded-xl p-4 flex gap-3 ${copy.cls}`} data-testid="proposal-response-banner">
      <Icon className="w-5 h-5 shrink-0 mt-0.5" />
      <div><p className="font-bold">{copy.title}</p><p className="text-sm">{copy.body}</p></div>
    </div>
  );
};
